import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import useConstants from "../../hooks/useConstants";
import { getLocations, createLocation, updateLocation } from "../../api/locations";
import { detailByZip } from "../../api/auth";

export default function LocationForm() {
	const { locationId } = useParams();
	const isEdit = !!locationId;
	const navigate = useNavigate();
	const { constants, loading: constantsLoading } = useConstants();
	const [loading, setLoading] = useState(isEdit);
	const [saving, setSaving] = useState(false);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			locationName: "",
			locationType: "",
			streetAddress: "",
			city: "",
			state: "",
			zip: "",
			locationPhone: "",
			locationContactPersonName: "",
		},
		validationSchema: yup.object({
			locationName: yup.string().required("Required"),
			locationType: yup.string().required("Required"),
			streetAddress: yup.string().required("Required"),
			city: yup.string().required("Required"),
			state: yup.string().required("Required"),
			zip: yup.string().required("Required"),
			locationPhone: yup.string().required("Required"),
			locationContactPersonName: yup.string().required("Required"),
		}),
		onSubmit: async (values) => {
			setSaving(true);
			const res = isEdit ? await updateLocation(locationId, values) : await createLocation(values);
			setSaving(false);
			if (res && !res.error) {
				toast.success(isEdit ? "Location updated" : "Location added");
				navigate("/locations");
			} else {
				toast.error(res?.msg || "Could not save location");
			}
		},
	});

	useEffect(() => {
		if (!isEdit) return;
		(async () => {
			const res = await getLocations();
			if (res && !res.error) {
				const found = (res.data || []).find((l) => l.id === locationId);
				if (found) formik.setValues({ ...found, locationType: found.locationType?.value || "" });
			}
			setLoading(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locationId]);

	const lookupZip = async () => {
		if (!formik.values.zip) return;
		const res = await detailByZip(formik.values.zip);
		if (res && !res.error && res.data) {
			formik.setFieldValue("city", res.data.city || formik.values.city);
			formik.setFieldValue("state", res.data.state || formik.values.state);
		}
	};

	if (loading || constantsLoading) return <Loader />;

	return (
		<div>
			<PageHeader title={isEdit ? "Edit Location" : "Add Location"} subtitle="Where should sellers deliver product?" />
			<form className="card p-4" style={{ maxWidth: 640 }} onSubmit={formik.handleSubmit}>
				<div className="mb-3">
					<label className="form-label">Location name</label>
					<input className="form-control" name="locationName" value={formik.values.locationName} onChange={formik.handleChange} />
				</div>
				<div className="mb-3">
					<label className="form-label">Location type</label>
					<select className="form-select" name="locationType" value={formik.values.locationType} onChange={formik.handleChange}>
						<option value="">Select type</option>
						{constants.locationTypes.map((opt) => (
							<option key={opt.id} value={opt.value}>{opt.Title}</option>
						))}
					</select>
				</div>
				<div className="mb-3">
					<label className="form-label">Street address</label>
					<input className="form-control" name="streetAddress" value={formik.values.streetAddress} onChange={formik.handleChange} />
				</div>
				<div className="row g-2 mb-3">
					<div className="col-4">
						<label className="form-label">ZIP</label>
						<input className="form-control" name="zip" value={formik.values.zip} onChange={formik.handleChange} onBlur={lookupZip} />
					</div>
					<div className="col-4">
						<label className="form-label">City</label>
						<input className="form-control" name="city" value={formik.values.city} onChange={formik.handleChange} />
					</div>
					<div className="col-4">
						<label className="form-label">State</label>
						<input className="form-control" name="state" value={formik.values.state} onChange={formik.handleChange} />
					</div>
				</div>
				<div className="row g-2 mb-4">
					<div className="col-6">
						<label className="form-label">Contact person</label>
						<input className="form-control" name="locationContactPersonName" value={formik.values.locationContactPersonName} onChange={formik.handleChange} />
					</div>
					<div className="col-6">
						<label className="form-label">Contact phone</label>
						<input className="form-control" name="locationPhone" value={formik.values.locationPhone} onChange={formik.handleChange} />
					</div>
				</div>
				<div className="d-flex gap-2">
					<button type="submit" className="btn-accent-solid" disabled={saving}>
						{saving ? "Saving..." : isEdit ? "Save Changes" : "Add Location"}
					</button>
					<button type="button" className="btn-outline" onClick={() => navigate("/locations")}>
						Cancel
					</button>
				</div>
			</form>
		</div>
	);
}
